// Run orchestration. A RUN drives one node end to end: open the connection,
// warm up, send n paced probes, collect echoes and the node's receive log,
// classify, and finalise. A MULTI-node run does this for each node STRICTLY
// SEQUENTIALLY -- open A, full run, close A, then open B -- so there is never
// concurrent probe traffic to two nodes (hard constraint 1).

import { decode, encode, type Header } from './protocol';
import { type ProbeConnection, type IceInfo } from './connection';
import {
  classifyPackets,
  computeLossPerSecond,
  computeMetrics,
  type RawSample,
} from './metrics';
import { Pacer } from './pacer';
import { type SignalingClient } from './signaling';
import {
  type NodeInfo,
  type PacketRecord,
  type RunResult,
  type ClientInfo,
  type RouteHop,
  type Settings,
} from './types';

/** Arguments the engine passes to a connection factory. */
export interface CreateConnectionArgs {
  nodeId: string;
  runId: number;
  signaling: SignalingClient;
  onFrame: (data: ArrayBuffer) => void;
  /** Force a single address family, or 'auto'. */
  ipMode?: 'auto' | 'ipv4' | 'ipv6';
}

/** Injectable dependencies so runs can be driven with fakes in tests. */
export interface EngineDeps {
  signaling: SignalingClient;
  createConnection: (args: CreateConnectionArgs) => ProbeConnection;
  makePacer: (pps: number) => Pacer;
  /** Monotonic clock in microseconds. */
  now: () => number;
  /** Promise that resolves after `ms`, cancellable via signal. */
  delay: (ms: number, signal?: { aborted: boolean }) => Promise<void>;
  /** Non-zero 32-bit run id generator. */
  randomRunId: () => number;
  /** Time to wait for the final recvlog after stop, in ms. Defaults to 2000. */
  drainMs?: number;
  /** Coordinator-provided client identity (public IP + ASN) for this session. */
  sessionInfo?: { clientIp?: string; asn?: number; asnOrg?: string };
  /** When true, request a reverse route trace from each node after its run. */
  traceroute?: boolean;
}

/** Progress and lifecycle callbacks for a run. */
export interface RunHooks {
  onNodeStart?: (nodeId: string) => void;
  onNodeDone?: (result: RunResult) => void;
  onProgress?: (p: {
    nodeId: string;
    sent: number;
    echoed: number;
    total: number;
    elapsedMs: number;
  }) => void;
}

/**
 * Decode a base64 receive-log bitmap into the list of received sequence numbers.
 * The bitmap is LSB-first within the window [fromSeq, fromSeq+8*len): the bit
 * for fromSeq+i lives in byte i>>3 at position i&7. Matches the node's encoding.
 */
export function decodeBitmapB64(b64: string, fromSeq: number): number[] {
  const bytes = base64ToBytes(b64);
  const out: number[] = [];
  for (let i = 0; i < bytes.length * 8; i++) {
    if ((bytes[i >> 3] & (1 << (i & 7))) !== 0) {
      out.push(fromSeq + i);
    }
  }
  return out;
}

const B64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** Minimal, environment-independent base64 -> bytes decoder. */
export function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/[^A-Za-z0-9+/=]/g, '');
  const len = clean.length;
  let pad = 0;
  if (len >= 1 && clean[len - 1] === '=') pad++;
  if (len >= 2 && clean[len - 2] === '=') pad++;
  const outLen = Math.floor((len * 3) / 4) - pad;
  const out = new Uint8Array(outLen < 0 ? 0 : outLen);
  let bits = 0;
  let acc = 0;
  let o = 0;
  for (let i = 0; i < len; i++) {
    const c = clean[i];
    if (c === '=') break;
    const v = B64_ALPHABET.indexOf(c);
    if (v < 0) continue;
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >> bits) & 0xff;
    }
  }
  return out;
}

/** Default non-zero 32-bit run id. */
export function defaultRandomRunId(): number {
  let r = Math.floor(Math.random() * 0xffffffff) >>> 0;
  if (r === 0) r = 1;
  return r;
}

/** Number of packets a run will send for the given settings. */
export function plannedPacketCount(settings: Settings): number {
  return Math.max(1, Math.round((settings.pps * settings.durationMs) / 1000));
}

/**
 * Run one measurement against a single node and return its result. Opens a fresh
 * connection, performs the WebRTC handshake by routing signalling into the
 * connection, sends the paced probes, gathers echoes and the receive log,
 * classifies every packet, and closes the connection before returning.
 */
export async function runNode(
  node: NodeInfo,
  settings: Settings,
  deps: EngineDeps,
  hooks: RunHooks = {},
  signal?: { aborted: boolean },
): Promise<RunResult> {
  hooks.onNodeStart?.(node.id);

  const runId = deps.randomRunId();
  const n = plannedPacketCount(settings);
  const sizes = settings.sizes.length > 0 ? settings.sizes : [32];

  // Per-sequence state.
  const sentUs = new Array<number>(n).fill(NaN);
  const notSent = new Array<boolean>(n).fill(false);
  const rttMs = new Array<number | null>(n).fill(null);
  const serverSeqArr = new Array<number | null>(n).fill(null);
  let echoed = 0;
  let sent = 0;

  // Receive-log aggregation.
  let nodeReceived: Set<number> | null = null;
  let recvlogFinal = false;

  const onFrame = (data: ArrayBuffer): void => {
    let h: Header;
    try {
      h = decode(data);
    } catch {
      return;
    }
    if (h.runId !== runId) return;
    const seq = h.seq;
    if (seq < 0 || seq >= n) return;
    if (notSent[seq] || rttMs[seq] !== null) return;
    if (!Number.isFinite(sentUs[seq])) return;
    rttMs[seq] = (deps.now() - sentUs[seq]) / 1000;
    serverSeqArr[seq] = h.serverSeq;
    echoed++;
  };

  let ice: IceInfo | null = null;
  let route: RouteHop[] | undefined;
  const conn = deps.createConnection({
    nodeId: node.id,
    runId,
    signaling: deps.signaling,
    onFrame,
    ipMode: settings.ipMode,
  });

  // Route inbound signalling for this node/run into the connection and recvlog
  // collector. Only one node is active at a time, so these listeners live only
  // for the duration of this run.
  const offAnswer = deps.signaling.on('answer', (m) => {
    if (m.nodeId === node.id && m.runId === runId) void conn.acceptAnswer(m.sdp);
  });
  const offIce = deps.signaling.on('ice', (m) => {
    if (m.nodeId === node.id && m.runId === runId) void conn.addRemoteIce(m.candidate);
  });
  const offRecv = deps.signaling.on('recvlog', (m) => {
    if (m.nodeId !== node.id || m.runId !== runId) return;
    const seqs = decodeBitmapB64(m.bitmapB64, m.fromSeq);
    if (nodeReceived === null) nodeReceived = new Set<number>();
    for (const s of seqs) nodeReceived.add(s);
    if (m.final) recvlogFinal = true;
  });

  const startedAtUs = deps.now();
  try {
    await conn.open();

    deps.signaling.start(node.id, runId, n, sizes, settings.pps, settings.durationMs);

    // Warmup: paced probes on a throwaway run id so the node discards them
    // (never echoed, never logged). Primes pacing and send buffers.
    if (settings.warmupMs > 0) {
      const warmupRunId = runId === 0xffffffff ? 1 : (runId + 1) >>> 0;
      const warmupCount = Math.max(
        1,
        Math.round((settings.pps * settings.warmupMs) / 1000),
      );
      const warmPacer = deps.makePacer(settings.pps);
      await warmPacer.run(
        warmupCount,
        (tick) => {
          const size = sizes[tick.seq % sizes.length];
          if (conn.bufferedAmount > 2 * size) return;
          conn.send(
            encode(
              { runId: warmupRunId, seq: tick.seq, tClientSend: deps.now(), tServerRecv: 0, serverSeq: 0 },
              size,
            ),
          );
        },
        signal,
      );
    }

    // Measured send loop.
    let lastProgressUs = deps.now();
    const pacer = deps.makePacer(settings.pps);
    await pacer.run(
      n,
      (tick) => {
        const seq = tick.seq;
        const size = sizes[seq % sizes.length];
        if (conn.bufferedAmount > 2 * size) {
          notSent[seq] = true;
          return;
        }
        const t = deps.now();
        sentUs[seq] = t;
        sent++;
        conn.send(
          encode({ runId, seq, tClientSend: t, tServerRecv: 0, serverSeq: 0 }, size),
        );
        if (hooks.onProgress && t - lastProgressUs >= 250_000) {
          lastProgressUs = t;
          hooks.onProgress({
            nodeId: node.id,
            sent,
            echoed,
            total: n,
            elapsedMs: (t - startedAtUs) / 1000,
          });
        }
      },
      signal,
    );

    deps.signaling.stop(node.id, runId);

    // Wait for late echoes and the authoritative final receive log.
    const drainMs = deps.drainMs ?? 2000;
    const deadlineUs = deps.now() + drainMs * 1000;
    while (!recvlogFinal && deps.now() < deadlineUs && !signal?.aborted) {
      await deps.delay(50, signal);
    }

    // Optional reverse route trace, while the connection is still up so the node
    // can resolve the selected candidate's client address.
    if (deps.traceroute && !signal?.aborted) {
      route = await requestTrace(deps, node.id, runId, signal);
    }
  } finally {
    offAnswer();
    offIce();
    offRecv();
    ice = await conn.iceInfo().catch(() => null);
    conn.close();
  }

  const samples: RawSample[] = [];
  for (let seq = 0; seq < n; seq++) {
    samples.push({
      seq,
      sentUs: sentUs[seq],
      notSent: notSent[seq],
      rttMs: rttMs[seq],
      serverSeq: serverSeqArr[seq],
    });
  }
  const packets: PacketRecord[] = classifyPackets(
    samples,
    nodeReceived,
    recvlogFinal,
    settings.acceptableDelayMs,
  );
  const metrics = computeMetrics(packets);
  const lossPerSecond = computeLossPerSecond(packets, startedAtUs, settings.durationMs);

  const clientInfo = buildClientInfo(ice, deps.sessionInfo);
  const result: RunResult = {
    nodeId: node.id,
    tier: node.tier,
    settings,
    startedAtUs,
    packets,
    metrics,
    lossPerSecond,
    clientInfo,
    route,
  };
  hooks.onNodeDone?.(result);
  return result;
}

/** Ask the node for a reverse route trace and resolve its hops (or undefined). */
function requestTrace(
  deps: EngineDeps,
  nodeId: string,
  runId: number,
  signal?: { aborted: boolean },
): Promise<RouteHop[] | undefined> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (hops?: RouteHop[]) => {
      if (done) return;
      done = true;
      off();
      resolve(hops);
    };
    const off = deps.signaling.on('mtrresult', (m) => {
      if (m.nodeId !== nodeId || m.runId !== runId) return;
      finish(m.error ? undefined : m.hops ?? []);
    });
    deps.signaling.mtr(nodeId, runId);
    void deps.delay(45000, signal).then(() => finish(undefined));
  });
}

/** Merge the ICE family (from getStats) with the coordinator session identity. */
function buildClientInfo(
  ice: IceInfo | null,
  session?: { clientIp?: string; asn?: number; asnOrg?: string },
): ClientInfo | undefined {
  const family = ice?.family ?? 'unknown';
  const clientIp = session?.clientIp || ice?.localIp;
  const asn = session?.asn && session.asn > 0 ? session.asn : undefined;
  const asnOrg = session?.asnOrg || undefined;
  if (family === 'unknown' && !clientIp && !asn && !ice?.remoteIp) return undefined;
  return { family, clientIp, nodeIp: ice?.remoteIp, asn, asnOrg };
}

/**
 * Run measurements against several nodes STRICTLY SEQUENTIALLY. Node A is opened,
 * fully measured and closed before node B is touched. There is deliberately no
 * concurrency here (constraint 1).
 */
export async function runMulti(
  nodes: readonly NodeInfo[],
  settings: Settings,
  deps: EngineDeps,
  hooks: RunHooks = {},
  signal?: { aborted: boolean },
): Promise<RunResult[]> {
  const results: RunResult[] = [];
  for (const node of nodes) {
    if (signal?.aborted) break;
    const result = await runNode(node, settings, deps, hooks, signal);
    results.push(result);
  }
  return results;
}
